import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PensionModal from '../components/PensionModal';
import PensionMassSetupModal from '../components/PensionMassSetupModal';
import PendingChanges from '../components/PendingChanges';
import LeaveModal from '../components/LeaveModal';
import SickModal from '../components/SickModal';
import UnpaidLeaveModal from '../components/UnpaidLeaveModal';
import AdditionModal from '../components/AdditionModal';
import DeductionModal from '../components/DeductionModal';
import BankDetailsModal from '../components/BankDetailsModal';
import AddressModal from '../components/AddressModal';
import PersonalDetailsModal from '../components/PersonalDetailsModal';
import ContractualChangeModal from '../components/ContractualChangeModal';
import SalaryChangeModal from '../components/SalaryChangeModal';
import EmployeeActionsPopover from '../components/EmployeeActionsPopover';
import Toast from '../components/Toast';
import { StarterFormsStrip, StarterFormsList } from '../components/StarterFormsCard';
import { calculatePensionContribution, formatCurrency, effectiveMonthlySalary, monthlyGrossForPension, isPartTime as isPartTimeEmp, partTimeFraction as partTimeFractionOf } from '../utils/pensionCalc';
import { taxMonthToName, isUKBankHoliday, personalDetailsSummary } from '../utils/constants';

function PensionBadge({ pension }) {
  // No pension record at all — admin hasn't set this up yet.
  // Previously this fell through to "Enrolled" which was a UI lie.
  if (!pension || pension.pension_status === null || pension.pension_status === undefined) {
    return <span className="text-gray-400" title="Pension status not set — open Pension Setup to configure">&#9675; Not set</span>;
  }
  if (pension.pension_status === 'not-eligible') {
    return <span className="text-gray-400" title="Not Eligible">&#9675; N/A</span>;
  }
  if (pension.pension_status === 'opted-out') {
    return <span className="text-yellow-500" title="Opted Out">&#9888; Opted Out</span>;
  }
  if (pension.pension_status === 'deferred') {
    return <span className="text-blue-500" title="Deferred — assessment postponed">&#9202; Deferred</span>;
  }
  if (pension.pension_status === 'exempt') {
    return <span className="text-indigo-500" title="Exempt from auto-enrolment">&#9675; Exempt</span>;
  }
  if (pension.pension_status === 'enrolled') {
    return <span className="text-green-600" title="Enrolled">&#10003; Enrolled</span>;
  }
  return <span className="text-gray-400" title="Unknown pension status">&#9675; Unknown</span>;
}

function PensionExpandedRow({ employee, pensionableAdditions = 0 }) {
  // Pro-rata-aware: part-time staff use their pro-rata pay, not the FTE salary.
  // Pensionable additions (e.g. a car allowance flagged to count) top up the base.
  const monthlyGross = monthlyGrossForPension(employee) + (pensionableAdditions || 0);
  const basis = employee.earnings_basis || 'qualifying';

  const employerAmt = calculatePensionContribution(
    employee.employer_type, employee.employer_value, monthlyGross, basis
  );
  const employeeAmt = calculatePensionContribution(
    employee.employee_type, employee.employee_value, monthlyGross, basis
  );

  return (
    <div className="p-4 bg-brand-50 dark:bg-gray-800 border-t dark:border-gray-700">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <p className="font-medium text-gray-700 dark:text-gray-300">Employer Contribution</p>
          <p className="text-gray-600 dark:text-gray-400">
            {employee.employer_type === 'percentage'
              ? `${employee.employer_value}%`
              : formatCurrency(employee.employer_value)
            } ({employee.employer_type})
          </p>
          <p className="text-brand-600 font-medium">{formatCurrency(employerAmt)} /month</p>
        </div>
        <div>
          <p className="font-medium text-gray-700 dark:text-gray-300">Employee Contribution</p>
          <p className="text-gray-600 dark:text-gray-400">
            {employee.employee_type === 'percentage'
              ? `${employee.employee_value}%`
              : formatCurrency(employee.employee_value)
            } ({employee.employee_type})
          </p>
          <p className="text-brand-600 font-medium">{formatCurrency(employeeAmt)} /month</p>
        </div>
        <div>
          <p className="font-medium text-gray-700 dark:text-gray-300">Earnings Basis</p>
          <p className="text-gray-600 dark:text-gray-400 capitalize">{basis}</p>
          {pensionableAdditions > 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-xs">incl. {formatCurrency(pensionableAdditions)} pensionable additions</p>
          )}
          <p className="text-brand-600 font-medium">Total: {formatCurrency(employerAmt + employeeAmt)} /month</p>
        </div>
      </div>
    </div>
  );
}

// taxMonthToName, getUKBankHolidays, isUKBankHoliday imported from constants

function isWorkingDay(date) {
  return date.getDay() !== 0 && date.getDay() !== 6 && !isUKBankHoliday(date);
}

function getPayDayDateObject(period) {
  if (!period || !period.pay_day_type) return null;

  const calMonth = ((period.tax_month + 2) % 12) + 1;
  const calYear = period.tax_month <= 9 ? period.tax_year : period.tax_year + 1;
  const lastDay = new Date(calYear, calMonth, 0).getDate();

  // When true, a pay day landing on a weekend or bank holiday is brought
  // forward to the previous working day. Undefined is treated as true to match
  // the company default. `last_working_day` always rolls back by definition.
  const adjust = period.pay_day_adjust !== false;

  let payDay;
  switch (period.pay_day_type) {
    case 'last_day_of_month':
      payDay = new Date(calYear, calMonth - 1, lastDay);
      if (adjust) while (!isWorkingDay(payDay)) payDay.setDate(payDay.getDate() - 1);
      break;
    case 'last_working_day': {
      payDay = new Date(calYear, calMonth - 1, lastDay);
      while (!isWorkingDay(payDay)) payDay.setDate(payDay.getDate() - 1);
      break;
    }
    case 'last_friday': {
      payDay = new Date(calYear, calMonth - 1, lastDay);
      while (payDay.getDay() !== 5) payDay.setDate(payDay.getDate() - 1);
      if (adjust && isUKBankHoliday(payDay)) {
        payDay.setDate(payDay.getDate() - 1);
        while (!isWorkingDay(payDay)) payDay.setDate(payDay.getDate() - 1);
      }
      break;
    }
    case 'specific_date': {
      const day = Math.min(period.pay_day_specific_date || 1, lastDay);
      payDay = new Date(calYear, calMonth - 1, day);
      if (adjust) while (!isWorkingDay(payDay)) payDay.setDate(payDay.getDate() - 1);
      break;
    }
    default:
      return null;
  }
  return payDay;
}

function formatDateLabel(date) {
  if (!date) return null;
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const d = date.getDate();
  const suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
  return `${dayNames[date.getDay()]} ${d}${suffix} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

function getPayDayDate(period) {
  return formatDateLabel(getPayDayDateObject(period));
}

function getSubmissionDeadline(period) {
  const payDayDate = getPayDayDateObject(period);
  if (!payDayDate) return null;
  const deadline = new Date(payDayDate);
  let workingDays = 0;
  while (workingDays < 5) {
    deadline.setDate(deadline.getDate() - 1);
    if (isWorkingDay(deadline)) workingDays++;
  }
  return deadline;
}

// If the configured pay day was brought forward off a weekend or bank holiday,
// returns { nominalLabel, reason } describing the original date; otherwise null.
// last_working_day is excluded — for that type the roll-back is the definition,
// not an adjustment worth flagging.
function getPayDayAdjustmentInfo(period) {
  if (!period || !period.pay_day_type || period.pay_day_type === 'last_working_day') return null;
  const adjusted = getPayDayDateObject(period);
  const nominal = getPayDayDateObject({ ...period, pay_day_adjust: false });
  if (!adjusted || !nominal || adjusted.getTime() === nominal.getTime()) return null;
  const day = nominal.getDay();
  const reason = (day === 0 || day === 6) ? 'a weekend' : 'a bank holiday';
  return { nominalLabel: formatDateLabel(nominal), reason };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, viewingAs } = useAuth();
  const commissionEnabled = user?.commission_enabled || false;
  const hourlyTrackingEnabled = user?.hourly_tracking_enabled || false;
  const isAdminViewing = !!viewingAs;
  const [employees, setEmployees] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [standingItems, setStandingItems] = useState([]);
  const [submittedItems, setSubmittedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPensions, setExpandedPensions] = useState({});
  const [pensionModal, setPensionModal] = useState(null);
  const [leaveModal, setLeaveModal] = useState(null);
  const [sickModal, setSickModal] = useState(null);
  const [unpaidLeaveModal, setUnpaidLeaveModal] = useState(null);
  const [sickRecords, setSickRecords] = useState([]);
  const [starterInvites, setStarterInvites] = useState([]);
  const [additionModal, setAdditionModal] = useState(null);
  const [deductionModal, setDeductionModal] = useState(null);
  const [bankDetailsModal, setBankDetailsModal] = useState(null);
  const [addressModal, setAddressModal] = useState(null);
  const [personalDetailsModal, setPersonalDetailsModal] = useState(null);
  const [contractualChangeModal, setContractualChangeModal] = useState(null);
  const [salaryChangeModal, setSalaryChangeModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  // Salary editing removed — changes go through Actions > Contractual Change
  const [editingCommission, setEditingCommission] = useState({});
  const [editingCommissionNote, setEditingCommissionNote] = useState({});
  const [commissionNotePopover, setCommissionNotePopover] = useState(null);
  const [editingHours, setEditingHours] = useState({});
  const [editingJobTitle, setEditingJobTitle] = useState({});
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [actionsPopover, setActionsPopover] = useState(null); // { emp, rect } | null
  const [undoConfirmId, setUndoConfirmId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortDir, setSortDir] = useState('asc'); // alphabetical sort by surname: 'asc' = A–Z, 'desc' = Z–A
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [amending, setAmending] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const searchTimerRef = useRef(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [pensionSchemeDefault, setPensionSchemeDefault] = useState(null);
  const [showPensionSchemeSetup, setShowPensionSchemeSetup] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoadError('');
      const [empRes, changesRes, standingRes, periodRes, sickRes, startersRes] = await Promise.all([
        api.get('/employees'),
        api.get('/pending-changes'),
        api.get('/pending-changes/standing').catch(() => ({ data: [] })),
        api.get('/submissions/current-period'),
        api.get('/employees/sick-records').catch(() => ({ data: [] })),
        // New starter forms out with employees. Non-fatal: the dashboard is
        // still usable without it.
        api.get('/starter-invites').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data.filter(e => e.status !== 'left'));
      setPendingChanges(changesRes.data);
      setStandingItems(standingRes.data);
      setCurrentPeriod(periodRes.data);
      setSickRecords(sickRes.data);
      setStarterInvites(startersRes.data || []);

      // One-off additions/deductions submitted for the current period flip to
      // status='submitted' and drop out of /pending-changes. Pull them back in so
      // the dashboard "this period" totals match the admin run sheet until the
      // period rolls. Distinct status from pending/standing, so no double-count.
      const periodId = periodRes.data?.id;
      if (periodId) {
        try {
          const submittedRes = await api.get('/pending-changes/submitted', { params: { period_id: periodId } });
          setSubmittedItems(submittedRes.data);
        } catch {
          setSubmittedItems([]);
        }
      } else {
        setSubmittedItems([]);
      }
      // Company pension scheme default — fetched in parallel; not fatal if it 404s
      // (e.g. legacy company with no pension records). Used to show the setup
      // banner and to pre-fill new starter enrolments.
      const companyId = empRes.data[0]?.company_id || user?.company_id;
      if (companyId) {
        try {
          const psRes = await api.get(`/pensions/company/${companyId}/scheme-default`);
          setPensionSchemeDefault(psRes.data);
        } catch (e) { /* non-fatal */ }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setLoadError('Failed to load data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const openActionsPopover = (emp, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setActionsPopover({ emp, rect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } });
  };

  // Dispatch a popover action onto the right modal setter or navigation.
  const handleActionSelect = (id, emp) => {
    setActionsPopover(null);
    if (id === 'addition') setAdditionModal(emp);
    else if (id === 'deduction') setDeductionModal(emp);
    else if (id === 'bank_details') setBankDetailsModal(emp);
    else if (id === 'address') setAddressModal(emp);
    else if (id === 'personal_details') setPersonalDetailsModal(emp);
    else if (id === 'pension') setPensionModal(emp);
    else if (id === 'parental_leave') setLeaveModal(emp);
    else if (id === 'sick') setSickModal(emp);
    else if (id === 'unpaid_leave') setUnpaidLeaveModal(emp);
    else if (id === 'contractual_change') setContractualChangeModal(emp);
    else if (id === 'record_leaver') navigate(`/leaver?employee=${emp.id}`);
  };

  const handleCommissionChange = async (employeeId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) {
      setToast({ message: 'Commission cannot be negative', type: 'error' });
      return;
    }
    // Save old value for rollback
    const oldValue = employees.find(e => e.id === employeeId)?.commission || 0;
    setEmployees(prev =>
      prev.map(e => e.id === employeeId ? { ...e, commission: numValue } : e)
    );
    try {
      await api.patch(`/employees/${employeeId}/commission`, { commission: numValue });
    } catch (err) {
      // Rollback on failure
      setEmployees(prev =>
        prev.map(e => e.id === employeeId ? { ...e, commission: oldValue } : e)
      );
      setToast({ message: 'Failed to update commission: ' + (err.response?.data?.error || err.message), type: 'error' });
    }
  };

  const handleCommissionNoteSave = async (employeeId) => {
    const draft = editingCommissionNote[employeeId];
    if (draft === undefined) return;
    const emp = employees.find(e => e.id === employeeId);
    const oldNote = emp?.commission_note || null;
    const cleaned = (draft || '').trim();
    const newNote = cleaned === '' ? null : cleaned;
    setEmployees(prev =>
      prev.map(e => e.id === employeeId ? { ...e, commission_note: newNote } : e)
    );
    try {
      await api.patch(`/employees/${employeeId}/commission`, {
        commission: emp?.commission ?? 0,
        note: newNote,
      });
      setCommissionNotePopover(null);
      setEditingCommissionNote(prev => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
    } catch (err) {
      setEmployees(prev =>
        prev.map(e => e.id === employeeId ? { ...e, commission_note: oldNote } : e)
      );
      setToast({ message: 'Failed to save note: ' + (err.response?.data?.error || err.message), type: 'error' });
    }
  };

  const handleCommissionSave = async (employeeId) => {
    const value = editingCommission[employeeId];
    if (value === undefined) return;
    await handleCommissionChange(employeeId, value);
    setEditingCommission(prev => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  const handleCommissionCancel = (employeeId) => {
    setEditingCommission(prev => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  // Hours tracking (same pattern as commission)
  const handleHoursChange = async (employeeId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) {
      setToast({ message: 'Hours cannot be negative', type: 'error' });
      return;
    }
    const oldValue = employees.find(e => e.id === employeeId)?.hours_this_period || 0;
    setEmployees(prev =>
      prev.map(e => e.id === employeeId ? { ...e, hours_this_period: numValue } : e)
    );
    try {
      await api.patch(`/employees/${employeeId}/hours`, { hours_this_period: numValue });
    } catch (err) {
      setEmployees(prev =>
        prev.map(e => e.id === employeeId ? { ...e, hours_this_period: oldValue } : e)
      );
      setToast({ message: 'Failed to update hours: ' + (err.response?.data?.error || err.message), type: 'error' });
    }
  };

  const handleHoursSave = async (employeeId) => {
    const value = editingHours[employeeId];
    if (value === undefined) return;
    await handleHoursChange(employeeId, value);
    setEditingHours(prev => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  const handleHoursCancel = (employeeId) => {
    setEditingHours(prev => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  const handleDeleteChange = async (changeId) => {
    setUndoConfirmId(changeId);
  };

  const confirmUndo = async () => {
    const changeId = undoConfirmId;
    setUndoConfirmId(null);
    try {
      await api.delete(`/pending-changes/${changeId}`);
      setPendingChanges(prev => prev.filter(c => c.id !== changeId));
      setToast({ message: 'Change removed successfully', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: 'Failed to undo change', type: 'error' });
      console.error('Failed to undo change:', err);
    }
  };

  // Helper: submit a change — admin viewing-as writes directly, clients create pending changes
  const submitChange = async (changeType, employeeId, changeData) => {
    const emp = employees.find(e => e.id === employeeId);
    const payload = {
      change_type: changeType,
      employee_id: employeeId,
      change_data: {
        employee_name: `${emp.first_name} ${emp.last_name}`,
        ...changeData,
      },
    };
    if (isAdminViewing) {
      await api.post('/pending-changes/admin-direct', payload);
      setToast({
        message: 'Applied directly (admin override). This does not create a pending change.',
        type: 'info',
      });
    } else {
      await api.post('/pending-changes', payload);
    }
  };

  const handlePensionSave = async (employeeId, pensionData) => {
    if (isAdminViewing) {
      await api.put(`/pensions/${employeeId}/admin`, pensionData);
    } else {
      await api.put(`/pensions/${employeeId}`, pensionData);
    }
    await loadData();
  };

  const handleLeaveSave = async (employeeId, leaveData) => {
    await submitChange('parental_leave', employeeId, leaveData);
    await loadData();
  };

  const handleSickSave = async (employeeId, sickData) => {
    await submitChange('sick_leave', employeeId, sickData);
    await loadData();
  };

  const handleUnpaidLeaveSave = async (employeeId, leaveData) => {
    await submitChange('unpaid_leave', employeeId, leaveData);
    await loadData();
  };

  const handleAdditionSave = async (employeeId, additionData) => {
    await submitChange('addition', employeeId, additionData);
    await loadData();
  };

  const handleDeductionSave = async (employeeId, deductionData) => {
    await submitChange('deduction', employeeId, deductionData);
    await loadData();
  };

  const handleBankDetailsSave = async (employeeId, bankData) => {
    await submitChange('bank_details', employeeId, {
      bank_account_name: bankData.bank_account_name,
      sort_code: bankData.sort_code,
      account_number: bankData.account_number,
    });
    await loadData();
  };

  const handleAddressSave = async (employeeId, addressData) => {
    await submitChange('address_change', employeeId, {
      address: addressData.address,
      address_line_2: addressData.address_line_2,
      city: addressData.city,
      postcode: addressData.postcode,
    });
    await loadData();
  };

  const handlePersonalDetailsSave = async (employeeId, changeData) => {
    await submitChange('personal_details', employeeId, changeData);
    await loadData();
  };

  const handleContractualChangeSave = async (employeeId, changeData) => {
    await submitChange('contractual_change', employeeId, changeData);
    await loadData();
  };

  const handleSalaryChangeSave = async (employeeId, changeData) => {
    await submitChange('salary', employeeId, changeData);
    await loadData();
  };

  const handleJobTitleSave = async (employeeId) => {
    const newTitle = editingJobTitle[employeeId];
    if (newTitle === undefined) return;
    const emp = employees.find(e => e.id === employeeId);
    const oldTitle = emp?.job_title || '';
    if (newTitle.trim() === oldTitle.trim()) {
      setEditingJobTitle(prev => { const next = { ...prev }; delete next[employeeId]; return next; });
      return;
    }
    try {
      await api.post('/pending-changes', {
        change_type: 'contractual_change',
        employee_id: employeeId,
        change_data: {
          employee_name: `${emp.first_name} ${emp.last_name}`,
          change_type: 'job_title_change',
          change_type_label: 'Job Title Change',
          new_job_title: newTitle.trim(),
          previous_job_title: oldTitle,
        },
      });
      setEditingJobTitle(prev => { const next = { ...prev }; delete next[employeeId]; return next; });
      setToast({ message: 'Job title change submitted', type: 'success' });
      await loadData();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to submit job title change', type: 'error' });
    }
  };

  const handleJobTitleCancel = (employeeId) => {
    setEditingJobTitle(prev => { const next = { ...prev }; delete next[employeeId]; return next; });
  };

  const togglePension = (id) => {
    setExpandedPensions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const [existingSubmission, setExistingSubmission] = useState(false);

  const handleSubmit = async () => {
    if (currentPeriod && currentPeriod.status !== 'open') {
      setToast({ message: `This payroll period has already been ${currentPeriod.status}.`, type: 'warning' });
      return;
    }
    // Check for existing unprocessed submission
    if (currentPeriod?.id) {
      try {
        const res = await api.get('/submissions', { params: { limit: 100 } });
        const existing = res.data.find(s => s.payroll_period_id === currentPeriod.id && !s.processed);
        setExistingSubmission(!!existing);
      } catch {
        setExistingSubmission(false);
      }
    }
    setShowReviewModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (existingSubmission) {
      if (!window.confirm('You already have a pending submission for this period. Are you sure you want to replace it?')) {
        return;
      }
    }
    setShowReviewModal(false);
    setSubmitting(true);
    try {
      const res = await api.post('/submissions', {
        payroll_period_id: currentPeriod?.id || null,
      });
      const s = res.data.summary;
      const parts = [];
      if (s.changes_count) parts.push(`${s.changes_count} change${s.changes_count !== 1 ? 's' : ''}`);
      if (s.hourly_count) parts.push(`${s.hourly_count} hourly ${s.hourly_count !== 1 ? 'entries' : 'entry'}`);
      if (s.commissions_count) parts.push(`${s.commissions_count} commission${s.commissions_count !== 1 ? 's' : ''}`);
      setSuccessMsg(
        `Submission successful!${parts.length > 0 ? ' ' + parts.join(', ') + ' submitted.' : ''}`
      );
      await loadData();
      setTimeout(() => setSuccessMsg(''), 9000);
    } catch (err) {
      setToast({ message: 'Submission failed: ' + (err.response?.data?.error || err.message), type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Debounce search query
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedQuery(value), 300);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading employees...</div>;
  }

  if (loadError) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-gray-700 dark:text-gray-300 font-medium">{loadError}</p>
        <button onClick={() => { setLoading(true); loadData(); }} className="mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700">
          Retry
        </button>
      </div>
    );
  }

  const hasChanges = pendingChanges.length > 0;
  const hasCommissions = employees.some(e => parseFloat(e.commission) > 0);
  const hasHourlyHours = employees.some(e => e.pay_type === 'hourly' && parseFloat(e.hours_this_period) > 0);
  const activeEmployees = employees.filter(e => e.status === 'active' || (e.status === 'pending_start' && e.pay_type === 'hourly'));
  const pendingStartEmployees = employees.filter(e => e.status === 'pending_start');
  const periodLocked = currentPeriod && currentPeriod.status !== 'open';
  const totalChangeCount = pendingChanges.length + employees.filter(e => parseFloat(e.commission) > 0).length + employees.filter(e => e.pay_type === 'hourly' && parseFloat(e.hours_this_period) > 0).length;
  const pendingChangeCount = pendingChanges.length;

  // #3: Search filter (debounced)
  const query = debouncedQuery.toLowerCase().trim();
  const searchedEmployees = query
    ? activeEmployees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(query) ||
        (emp.job_title || '').toLowerCase().includes(query)
      )
    : activeEmployees;
  // Alphabetical sort by surname, then forename, with the toggle controlling direction.
  const filteredEmployees = [...searchedEmployees].sort((a, b) => {
    const aKey = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
    const bKey = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
    const cmp = aKey.localeCompare(bKey);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Per-employee additions for this period: admin standing setup items + client-
  // submitted pending additions. Mirrors the employee profile — the gross/total folds
  // in all additions, while the pension base only counts pensionable ones.
  const todayStr = new Date().toISOString().split('T')[0];
  const parseCD = (c) => (typeof c.change_data === 'string' ? JSON.parse(c.change_data) : (c.change_data || {}));
  const standingActive = (d) => !d.cancelled_at && !(d.recurring && d.recurring_end_date && d.recurring_end_date < todayStr);
  const additionsTotalByEmp = new Map();
  const pensionableAddByEmp = new Map();
  const addAddition = (empId, d) => {
    const amt = parseFloat(d.amount) || 0;
    if (!empId || !(amt > 0)) return;
    additionsTotalByEmp.set(empId, (additionsTotalByEmp.get(empId) || 0) + amt);
    if (d.pensionable !== false && d.addition_type !== 'expense_reimbursement') {
      pensionableAddByEmp.set(empId, (pensionableAddByEmp.get(empId) || 0) + amt);
    }
  };
  for (const c of standingItems) {
    if (c.change_type !== 'addition') continue;
    const d = parseCD(c);
    if (standingActive(d)) addAddition(c.employee_id, d);
  }
  for (const c of pendingChanges) {
    if (c.change_type === 'addition') addAddition(c.employee_id, parseCD(c));
  }
  for (const c of submittedItems) {
    if (c.change_type === 'addition') addAddition(c.employee_id, parseCD(c));
  }

  // Compute summary card data
  const salariesTotal = activeEmployees.reduce((sum, e) => sum + effectiveMonthlySalary(e), 0);
  const commissionTotal = activeEmployees.reduce((sum, e) => sum + (parseFloat(e.commission) || 0), 0);
  const additionsTotal = activeEmployees.reduce((sum, e) => sum + (additionsTotalByEmp.get(e.id) || 0), 0);
  const anyAdditions = additionsTotal > 0;
  const totalMonthlyPayroll = salariesTotal + commissionTotal + additionsTotal;

  // Total monthly pension contributions (employer + employee) across enrolled employees.
  const monthlyPensionTotal = activeEmployees.reduce((sum, e) => {
    if (e.pension_status !== 'enrolled') return sum;
    const mGross = monthlyGrossForPension(e) + (pensionableAddByEmp.get(e.id) || 0);
    const basis = e.earnings_basis || 'qualifying';
    const er = calculatePensionContribution(e.employer_type, e.employer_value, mGross, basis) || 0;
    const ee = calculatePensionContribution(e.employee_type, e.employee_value, mGross, basis) || 0;
    return sum + er + ee;
  }, 0);

  const payDayLabel = currentPeriod ? getPayDayDate(currentPeriod) : null;
  const payDayAdjustment = currentPeriod ? getPayDayAdjustmentInfo(currentPeriod) : null;
  const submissionDeadline = currentPeriod ? getSubmissionDeadline(currentPeriod) : null;
  const deadlineLabel = submissionDeadline ? formatDateLabel(submissionDeadline) : null;
  const now = new Date();
  const daysUntilDeadline = submissionDeadline
    ? Math.ceil((submissionDeadline.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Build a set of employee IDs with pending salary changes (direct or via contractual change)
  const salaryChangedIds = new Set(
    pendingChanges
      .filter(c => {
        if (c.change_type === 'salary' && c.employee_id) return true;
        if (c.change_type === 'contractual_change' && c.employee_id) {
          const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
          return d?.change_type === 'salary_change';
        }
        return false;
      })
      .map(c => c.employee_id)
  );

  return (
    <div className="max-w-[1100px] mx-auto px-8 py-7">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      {/* Hero strip — period + deadline + submit CTA */}
      {currentPeriod && (
        <div className="rounded-2xl p-6 mb-5 shadow-lg grid items-center gap-6 text-white"
          style={{ gridTemplateColumns: '1fr auto', background: 'linear-gradient(135deg, #b877f4 0%, #9345d6 100%)' }}>
          <div>
            <p className="text-[12px] uppercase tracking-wider opacity-80">
              {taxMonthToName(currentPeriod.tax_month)} {currentPeriod.tax_year} · Tax M{currentPeriod.tax_month} {currentPeriod.tax_year}/{(currentPeriod.tax_year + 1).toString().slice(-2)}
            </p>
            <h1 className="font-heading leading-tight mt-1" style={{ fontSize: 32 }}>
              {payDayLabel ? (
                <>Pay day is {payDayLabel}{payDayAdjustment && <span className="text-accent" title={`Normally ${payDayAdjustment.nominalLabel}`}>*</span>}</>
              ) : 'Pay day not yet configured'}
            </h1>
            {deadlineLabel && currentPeriod.status === 'open' && (
              <p className="text-[15px] mt-2 opacity-90 flex flex-wrap items-center gap-2">
                <span>Deadline to submit: <strong className="inline-flex items-center px-2 py-0.5 rounded-md bg-accent text-brand-900 font-semibold">{deadlineLabel}</strong></span>
                {daysUntilDeadline !== null && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[12.5px] font-semibold ${
                    daysUntilDeadline <= 0
                      ? 'bg-accent text-brand-900'
                      : 'bg-white/15 text-white ring-1 ring-white/20'
                  }`}>
                    {daysUntilDeadline < 0
                      ? `${Math.abs(daysUntilDeadline)} day${Math.abs(daysUntilDeadline) !== 1 ? 's' : ''} overdue`
                      : daysUntilDeadline === 0
                        ? 'Due today'
                        : `${daysUntilDeadline} working day${daysUntilDeadline !== 1 ? 's' : ''} from today`}
                  </span>
                )}
              </p>
            )}
            {payDayAdjustment && payDayLabel && currentPeriod.status === 'open' && (
              <p className="text-[12.5px] mt-1 opacity-75">
                * Normally {payDayAdjustment.nominalLabel} — brought forward as it falls on {payDayAdjustment.reason}.
              </p>
            )}
            {currentPeriod.status === 'submitted' && (<p className="text-[15px] mt-2 opacity-90">This period has been submitted.</p>)}
            {currentPeriod.status === 'locked' && (<p className="text-[15px] mt-2 opacity-90">This period is closed — no further changes can be submitted.</p>)}
          </div>
          <div className="text-right shrink-0">
            <p className="text-[12px] uppercase tracking-wider opacity-80">{periodLocked ? 'Status' : 'Pending'}</p>
            <p className="font-bold tabular leading-none mt-1" style={{ fontSize: 44 }}>
              {periodLocked ? '—' : `${totalChangeCount}`}
            </p>
            {!periodLocked && (
              <p className="text-[12px] opacity-80 mt-1">change{totalChangeCount !== 1 ? 's' : ''}</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting || periodLocked}
              className={`mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition bg-accent text-brand-900 ${
                periodLocked ? 'cursor-not-allowed' : 'hover:bg-accent-dark disabled:opacity-50'
              }`}
            >
              <span>
                {submitting
                  ? 'Submitting…'
                  : periodLocked
                    ? 'Period closed'
                    : hasChanges || hasCommissions || hasHourlyHours
                      ? 'Submit changes →'
                      : 'Confirm — no changes →'}
              </span>
              {!submitting && !periodLocked && totalChangeCount > 0 && (
                <span className="text-[12px] font-bold px-2 py-0.5 rounded-full bg-brand-900 text-white">{totalChangeCount}</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* New starter forms still out with employees. A completed one is a
          payroll change now, so it is counted by the changes card above and
          drops out of here. */}
      <StarterFormsStrip invites={starterInvites} />

      {/* Stat row — 4 tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
          <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Employees</p>
          <p className="font-heading text-[24px] tabular leading-none mt-1.5 text-brand-700">{activeEmployees.length}</p>
          {pendingStartEmployees.length > 0 && (
            <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />{pendingStartEmployees.length} pending start
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
          <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Monthly gross</p>
          <p className="font-heading text-[24px] tabular leading-none mt-1.5 text-emerald-600">
            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.ceil(totalMonthlyPayroll))}
          </p>
          {commissionTotal > 0 && (
            <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />incl. {formatCurrency(commissionTotal)} commission
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
          <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Pension monthly</p>
          <p className="font-heading text-[24px] tabular leading-none mt-1.5 text-sky-600">
            {monthlyPensionTotal > 0
              ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(monthlyPensionTotal))
              : '—'}
          </p>
          <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />employer + employee
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
          <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Next pay day</p>
          <p className="font-heading text-[20px] leading-none mt-1.5 text-amber-700">{payDayLabel || 'Not configured'}</p>
          <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{periodLocked ? 'period closed' : 'this period'}
          </p>
        </div>
      </div>

      {/* Pension Setup Banner. Fires on either:
          (a) company has actually-enrolled employees AND no default scheme type set
          (b) company has employees whose pension status was never configured
          The same modal handles both — we tailor the wording to the priority case. */}
      {pensionSchemeDefault && (() => {
        const enrolledCount = (pensionSchemeDefault.enrolled_employees || []).length;
        const notSetCount = pensionSchemeDefault.not_set_count || 0;
        const def = pensionSchemeDefault.default_pension_scheme_type;
        const defaultMissing = !def;
        // Inconsistency: company says no pension scheme but employees are
        // still flagged enrolled — admin needs to reconcile.
        const noneWithEnrolled = def === 'none' && enrolledCount > 0;
        const needsAction = (defaultMissing && enrolledCount > 0) || notSetCount > 0 || noneWithEnrolled;
        if (!needsAction) return null;
        let headline, body;
        if (noneWithEnrolled) {
          headline = `${enrolledCount} employee${enrolledCount === 1 ? ' is' : 's are'} enrolled but company has no pension scheme`;
          body = 'Either change the company default to a real scheme type, or change these employees to Opted Out / Not Eligible.';
        } else if (notSetCount > 0) {
          headline = `${notSetCount} employee${notSetCount === 1 ? '' : 's'} have no pension status set`;
          body = 'Open Pension Setup to mark each as enrolled / opted-out / not eligible — payroll can\'t compute contributions until this is set.';
        } else {
          headline = 'Pension scheme type not set';
          body = `${enrolledCount} enrolled employee${enrolledCount === 1 ? '' : 's'} — pick salary sacrifice / net pay / relief at source so payroll knows pre-tax vs post-tax.`;
        }
        return (
          <div className="rounded-lg border p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">{headline}</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">{body}</p>
              </div>
            </div>
            <button
              onClick={() => setShowPensionSchemeSetup(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 whitespace-nowrap"
            >
              Set up
            </button>
          </div>
        );
      })()}


      {successMsg && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
          {successMsg}{' '}
          You can view everything you've submitted anytime in your{' '}
          <button
            onClick={() => navigate('/history')}
            className="font-semibold underline hover:text-green-900"
          >
            submission history
          </button>.
        </div>
      )}

      {currentPeriod && currentPeriod.status === 'submitted' && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-center justify-between">
          <span>This period has been submitted. Need to make additional changes before processing?</span>
          <button
            onClick={() => setShowAmendModal(true)}
            className="ml-4 px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 whitespace-nowrap"
          >
            Amend Submission
          </button>
        </div>
      )}

      {showAmendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20 rounded-t-xl">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Amend Submission</h3>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This will reopen the payroll period so you can make further changes. The accountant has not yet processed it.
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                All previously submitted changes will be restored and visible, so you can review and undo them individually if needed.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The previous submission will be removed from history and replaced when you resubmit.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowAmendModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                disabled={amending}
                onClick={async () => {
                  setAmending(true);
                  try {
                    await api.patch(`/submissions/periods/${currentPeriod.id}/reopen`);
                    setShowAmendModal(false);
                    setToast({ message: 'Period reopened successfully. You can now make changes.', type: 'success' });
                    await loadData();
                  } catch (err) {
                    setToast({ message: err.response?.data?.error || 'Failed to reopen period', type: 'error' });
                  } finally {
                    setAmending(false);
                  }
                }}
                className="px-5 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {amending ? 'Reopening...' : 'Reopen & Amend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {currentPeriod && currentPeriod.status === 'locked' && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          This payroll period has been locked. No further changes can be submitted.
        </div>
      )}

      {/* Pending changes bar — confident amber-100/300 with Review CTA */}
      {pendingChangeCount > 0 && (() => {
        const commissionEntries = employees.filter(e => parseFloat(e.commission) > 0);
        const periodStatus = currentPeriod?.status;
        const isSubmitted = periodStatus === 'submitted';
        const isLocked = periodStatus === 'locked';

        const tone = isLocked
          ? { bar: 'bg-sky-100 ring-sky-300', circle: 'bg-sky-500', text: 'text-sky-950', sub: 'text-sky-800', cta: 'bg-sky-600 hover:bg-sky-700' }
          : isSubmitted
            ? { bar: 'bg-amber-100 ring-amber-300', circle: 'bg-amber-500', text: 'text-amber-950', sub: 'text-amber-800', cta: 'bg-amber-600 hover:bg-amber-700' }
            : { bar: 'bg-amber-100 ring-amber-300', circle: 'bg-amber-500', text: 'text-amber-950', sub: 'text-amber-800', cta: 'bg-amber-600 hover:bg-amber-700' };

        const message = isLocked
          ? `${pendingChangeCount} change${pendingChangeCount !== 1 ? 's' : ''} queued for next period`
          : isSubmitted
            ? `${pendingChangeCount} new change${pendingChangeCount !== 1 ? 's' : ''} since submission`
            : `${pendingChangeCount} pending change${pendingChangeCount !== 1 ? 's' : ''} — not yet submitted`;

        // Summarise the pending changes by type for the sub-line. Counts per type
        // stay compact and scale (e.g. "3 new starters · 2 salary changes"), and avoid
        // the per-person list collapsing to "Change · leaver" when names aren't loaded.
        const TYPE_LABELS = {
          new_starter: ['new starter', 'new starters'],
          leaver: ['leaver', 'leavers'],
          pension: ['pension update', 'pension updates'],
          salary: ['salary change', 'salary changes'],
          parental_leave: ['parental leave', 'parental leave'],
          sick_leave: ['sick leave', 'sick leave'],
          unpaid_leave: ['unpaid leave', 'unpaid leave'],
          addition: ['addition', 'additions'],
          deduction: ['deduction', 'deductions'],
          bank_details: ['bank-details update', 'bank-details updates'],
          address_change: ['address change', 'address changes'],
          personal_details: ['personal details update', 'personal details updates'],
          contractual_change: ['contractual change', 'contractual changes'],
        };
        const typeCounts = pendingChanges.reduce((acc, c) => {
          const t = c.change_type || 'other';
          acc[t] = (acc[t] || 0) + 1;
          return acc;
        }, {});
        const typeSummary = Object.entries(typeCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([type, n]) => {
            const labels = TYPE_LABELS[type];
            const single = labels ? labels[0] : type.replace(/_/g, ' ');
            const plural = labels ? labels[1] : `${type.replace(/_/g, ' ')}s`;
            return `${n} ${n === 1 ? single : plural}`;
          });
        const shownSummary = typeSummary.slice(0, 4);
        const extraTypes = typeSummary.length - shownSummary.length;

        return (
          <div className="mb-4">
            <div className={`rounded-xl ring-1 ${tone.bar} px-5 py-4 shadow-sm flex items-center gap-4`}>
              <span className={`shrink-0 grid place-items-center w-9 h-9 rounded-full text-white text-[14px] font-bold ${tone.circle}`}>
                {pendingChangeCount}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-[15px] font-bold ${tone.text}`}>{message}</p>
                {shownSummary.length > 0 && (
                  <p className={`text-[13px] mt-0.5 ${tone.sub} truncate`}>
                    {shownSummary.join(' · ')}
                    {extraTypes > 0 && (
                      <span className="opacity-60"> · +{extraTypes} more type{extraTypes !== 1 ? 's' : ''}</span>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={() => setChangesExpanded(v => !v)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white shadow-sm ${tone.cta}`}
              >
                {changesExpanded ? 'Hide changes' : 'Review changes →'}
              </button>
            </div>
            {changesExpanded && (
              <div className="mt-2">
                <PendingChanges changes={pendingChanges} onDelete={handleDeleteChange} commissionEntries={commissionEntries} onUpdate={loadData} />
              </div>
            )}
          </div>
        );
      })()}

      {pendingStartEmployees.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          {pendingStartEmployees.length} new starter{pendingStartEmployees.length !== 1 ? 's' : ''} will be added when you submit
        </div>
      )}

      {/* Employees section header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-1 h-5 bg-brand-500 rounded-full" />
          <h2 className="font-heading text-[18px] tracking-[0.06em] uppercase text-ink dark:text-gray-100">Employees</h2>
          <span className="text-gray-400 tabular text-[14px]">({activeEmployees.length})</span>
          {pensionSchemeDefault?.company_id && (
            <button
              onClick={() => setShowPensionSchemeSetup(true)}
              className="text-[12px] font-medium text-brand-700 dark:text-brand-400 hover:text-brand-800 px-2 py-1 rounded ring-1 ring-brand-200 hover:bg-brand-50 inline-flex items-center gap-1"
              title="Open Pension Setup"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Pension Setup
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeEmployees.length > 0 && (
            <div className="relative w-[220px]">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search employees…"
                className="w-full pl-9 pr-8 py-2 text-[13.5px] rounded-md ring-1 ring-gray-200 focus:ring-brand-400 focus:outline-none bg-white dark:bg-gray-800 dark:text-gray-100"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setDebouncedQuery(''); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          )}
          {activeEmployees.length > 1 && (
            <button
              onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
              title={`Sorted by name ${sortDir === 'asc' ? 'A–Z' : 'Z–A'} (click to reverse)`}
              aria-label={`Sort employees by name, currently ${sortDir === 'asc' ? 'A to Z' : 'Z to A'}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h12M3 12h8M3 18h5"/><path d="M16 16l3 3 3-3M19 6v13"/></svg>
              {sortDir === 'asc' ? 'A–Z' : 'Z–A'}
            </button>
          )}
          <button
            onClick={() => navigate('/new-starter')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[13px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            New starter
          </button>
          <button
            onClick={() => navigate('/leaver')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[13px] font-semibold text-white bg-rose-600 hover:bg-rose-700 shadow-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Record leaver
          </button>
        </div>
      </div>
      {searchQuery && activeEmployees.length > 0 && (
        <p className="text-[12px] text-gray-500 -mt-2 mb-3 ml-1">Showing {filteredEmployees.length} of {activeEmployees.length} employees</p>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          {/* #4: Merged salary columns (Annual + Monthly -> one "Salary" column) */}
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{hourlyTrackingEnabled ? 'Pay' : 'Salary'}</th>
                {hourlyTrackingEnabled && <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>}
                {commissionEnabled && <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commission</th>}
                {anyAdditions && <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Additions</th>}
                <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Monthly</th>
                <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pension</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredEmployees.map(emp => {
                const isHourly = emp.pay_type === 'hourly';
                // Prefer the period-effective salary from the salary_history table when available.
                // Falls back to the live annual_salary for back-compat with employees pre-dating the migration.
                const periodEffective = emp.effective_annual_salary !== undefined && emp.effective_annual_salary !== null
                  ? parseFloat(emp.effective_annual_salary)
                  : null;
                const annualVal = periodEffective !== null ? periodEffective : (parseFloat(emp.annual_salary) || 0);
                // Derive the pro-rata figure from the period-effective FTE and the
                // working pattern rather than reading prorata_salary. That column is a
                // cache: a salary change recorded in history may not have been written
                // back to it yet, and displaying the stale value alongside a correct
                // monthly figure is how "£48,000 pro-rata / £4,400 a month" happens.
                const ptFrac = partTimeFractionOf(emp);
                const isPartTime = isPartTimeEmp(emp) && ptFrac < 1;
                const proRataVal = isPartTime ? annualVal * ptFrac : 0;
                const effectiveAnnual = isPartTime ? proRataVal : annualVal;
                const hourlyRate = parseFloat(emp.hourly_rate) || 0;
                const hoursThisPeriod = parseFloat(emp.hours_this_period) || 0;
                const monthlyVal = isHourly ? (hourlyRate * hoursThisPeriod) : (effectiveAnnual / 12);
                const commission = parseFloat(emp.commission) || 0;
                const additionsForEmp = additionsTotalByEmp.get(emp.id) || 0;
                const totalMonthly = monthlyVal + commission + additionsForEmp;
                const isEditingComm = editingCommission[emp.id] !== undefined;
                const isEditingHrs = editingHours[emp.id] !== undefined;

                return (
                  <React.Fragment key={emp.id}>
                    <tr className={`[&>td]:align-middle ${
                      actionsPopover?.emp.id === emp.id
                        ? 'bg-brand-50/60 transition'
                        : actionsPopover
                          ? 'opacity-40 transition'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        <span
                          className="cursor-pointer hover:text-brand-600 hover:underline"
                          onClick={() => navigate(`/employee/${emp.id}`)}
                          title="View employee profile"
                        >
                          {emp.first_name} {emp.last_name}
                        </span>
                        {emp.is_director && <span className="ml-1 text-xs text-brand-600">(Director)</span>}
                        {emp.status === 'pending_start' && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                            New Starter
                          </span>
                        )}
                        {sickRecords.find(r => r.employee_id === emp.id) && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            Off Sick
                          </span>
                        )}
                        {emp.leaving_date && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            Leaving {new Date(emp.leaving_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                        {emp.job_title && <span className="block lg:hidden text-xs font-normal text-gray-500 mt-0.5">{emp.job_title}</span>}
                        {emp.pension_status && (
                          <span className="sm:hidden inline-flex items-center text-xs mt-0.5">
                            <PensionBadge pension={emp} />
                          </span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-600">
                        {editingJobTitle[emp.id] !== undefined ? (
                          <div className="flex items-center space-x-1">
                            <input
                              type="text"
                              value={editingJobTitle[emp.id]}
                              onChange={(e) => setEditingJobTitle(prev => ({ ...prev, [emp.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleJobTitleSave(emp.id); if (e.key === 'Escape') handleJobTitleCancel(emp.id); }}
                              className="w-40 text-sm border-gray-300 rounded focus:border-brand-500 focus:ring-brand-500"
                              autoFocus
                            />
                            <button onClick={() => handleJobTitleSave(emp.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">Save</button>
                            <button onClick={() => handleJobTitleCancel(emp.id)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                          </div>
                        ) : (
                          <span
                            className="cursor-pointer hover:text-brand-600 hover:underline"
                            onClick={() => setEditingJobTitle(prev => ({ ...prev, [emp.id]: emp.job_title || '' }))}
                            title="Click to edit job title"
                          >
                            {emp.job_title || <span className="text-gray-300 italic">No title</span>}
                          </span>
                        )}
                      </td>

                      {/* #4: Pay column — salary or hourly rate */}
                      <td className="px-4 py-3 text-sm text-right">
                        {isHourly ? (
                          <span
                            className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded px-1 py-0.5 -mx-1 transition-colors"
                            onClick={() => setSalaryChangeModal(emp)}
                            title="Click to change hourly rate"
                          >
                            {/* Invisible mirror of the sub-lines so the rate sits on the row's
                                centre line (align-middle), leaving only the breakdown below. */}
                            <span aria-hidden="true" className="block text-xs invisible">{parseFloat(emp.contracted_hours_per_week) > 0 ? `${emp.contracted_hours_per_week} hrs/wk` : 'Zero hours'}</span>
                            <span aria-hidden="true" className="block text-xs invisible mt-0.5">Hourly</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatCurrency(hourlyRate)}/hr
                            </span>
                            <span className="block text-xs text-gray-400">{parseFloat(emp.contracted_hours_per_week) > 0 ? `${emp.contracted_hours_per_week} hrs/wk` : 'Zero hours'}</span>
                            <span className="block text-xs text-amber-600 dark:text-amber-400 mt-0.5">Hourly</span>
                          </span>
                        ) : (
                          <span
                            className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded px-1 py-0.5 -mx-1 transition-colors"
                            onClick={() => setSalaryChangeModal(emp)}
                            title="Click to change salary"
                          >
                            {/* Invisible mirror of the sub-lines so the salary figure sits on the
                                row's centre line (align-middle), leaving only the breakdown below. */}
                            <span aria-hidden="true" className="block text-xs invisible">{formatCurrency(effectiveAnnual / 12)}/mo</span>
                            {isPartTime && (
                              <span aria-hidden="true" className="block text-xs invisible mt-0.5">FTE: {formatCurrency(annualVal, { decimals: 'auto' })}</span>
                            )}
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatCurrency(isPartTime ? proRataVal : annualVal, { decimals: 'auto' })}
                              {isPartTime && (
                                <span className="ml-1 text-xs font-normal text-brand-600 dark:text-brand-400" title="Pro-rata for part-time hours">pro-rata</span>
                              )}
                              {salaryChangedIds.has(emp.id) && (
                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded" title="Salary changed this period">
                                  Changed
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-gray-400">{formatCurrency(effectiveAnnual / 12)}/mo</span>
                            {isPartTime && (
                              <span className="block text-xs text-gray-400 mt-0.5" title="Full-time equivalent salary">
                                FTE: {formatCurrency(annualVal, { decimals: 'auto' })}
                              </span>
                            )}
                          </span>
                        )}
                      </td>

                      {/* Hours column — inline edit for hourly employees */}
                      {hourlyTrackingEnabled && (
                      <td className="hidden lg:table-cell px-4 py-3 text-right text-sm">
                        {isHourly ? (
                          isEditingHrs ? (
                            <div>
                              <div className="flex items-center justify-end space-x-1">
                                <input
                                  type="number" step="0.5" min="0" inputMode="decimal"
                                  autoFocus
                                  value={editingHours[emp.id]}
                                  onChange={(e) => setEditingHours(prev => ({ ...prev, [emp.id]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleHoursSave(emp.id);
                                    if (e.key === 'Escape') handleHoursCancel(emp.id);
                                  }}
                                  className="w-20 text-right text-sm border-gray-300 rounded focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                                <span className="text-gray-400 text-xs">hrs</span>
                              </div>
                              <div className="flex justify-end space-x-2 mt-1">
                                <button onClick={() => handleHoursSave(emp.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">Save</button>
                                <button onClick={() => handleHoursCancel(emp.id)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                              </div>
                            </div>
                          ) : hoursThisPeriod > 0 ? (
                            <span
                              className="cursor-pointer hover:text-brand-600 hover:underline text-gray-900 dark:text-gray-100 font-medium"
                              onClick={() => setEditingHours(prev => ({ ...prev, [emp.id]: String(hoursThisPeriod) }))}
                              title="Click to edit hours"
                            >
                              {parseFloat(hoursThisPeriod).toFixed(2)} hrs
                            </span>
                          ) : (
                            <button
                              onClick={() => setEditingHours(prev => ({ ...prev, [emp.id]: '0' }))}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                            >
                              Enter hours
                            </button>
                          )
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      )}

                      {/* #5: Commission click-to-edit (only if commission enabled for this company) */}
                      {commissionEnabled && (
                      <td className="hidden lg:table-cell px-4 py-3 text-right text-sm">
                        {isEditingComm ? (
                          <div>
                            <div className="flex items-center justify-end space-x-1">
                              <span className="text-gray-400 text-xs">£</span>
                              <input
                                type="number" step="0.01" min="0" inputMode="decimal"
                                autoFocus
                                value={editingCommission[emp.id]}
                                onChange={(e) => setEditingCommission(prev => ({ ...prev, [emp.id]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleCommissionSave(emp.id);
                                  if (e.key === 'Escape') handleCommissionCancel(emp.id);
                                }}
                                className="w-24 text-right text-sm border-gray-300 rounded focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              />
                            </div>
                            <div className="flex justify-end space-x-2 mt-1">
                              <button onClick={() => handleCommissionSave(emp.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">Save</button>
                              <button onClick={() => handleCommissionCancel(emp.id)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative inline-flex items-center justify-end space-x-1">
                            <span
                              className={`cursor-pointer hover:text-brand-600 hover:underline ${commission > 0 ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-400'}`}
                              onClick={() => setEditingCommission(prev => ({ ...prev, [emp.id]: String(commission) }))}
                              title="Click to edit commission"
                            >
                              {commission > 0 ? formatCurrency(commission) : '-'}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setCommissionNotePopover(commissionNotePopover === emp.id ? null : emp.id);
                                setEditingCommissionNote(prev => ({ ...prev, [emp.id]: emp.commission_note || '' }));
                              }}
                              className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                emp.commission_note ? 'text-brand-600' : 'text-gray-300 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400'
                              }`}
                              title={emp.commission_note ? 'Edit note' : 'Add note'}
                              aria-label={emp.commission_note ? 'Edit commission note' : 'Add commission note'}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 21l1.3-3.9A8.964 8.964 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              {emp.commission_note && <span className="sr-only">Note present</span>}
                            </button>
                            {commissionNotePopover === emp.id && (
                              <div className="absolute right-0 top-full mt-2 z-30 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-left">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                  Commission note
                                </label>
                                <textarea
                                  rows={3}
                                  maxLength={1000}
                                  autoFocus
                                  value={editingCommissionNote[emp.id] ?? ''}
                                  onChange={(e) => setEditingCommissionNote(prev => ({ ...prev, [emp.id]: e.target.value }))}
                                  placeholder="Enter any key information about this commission, e.g. previous amounts advanced to be deducted etc."
                                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:text-white"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">Visible to admins on the submission. Max 1000 characters.</p>
                                <div className="flex justify-end space-x-2 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCommissionNotePopover(null);
                                      setEditingCommissionNote(prev => {
                                        const next = { ...prev };
                                        delete next[emp.id];
                                        return next;
                                      });
                                    }}
                                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleCommissionNoteSave(emp.id)}
                                    className="text-xs px-2 py-1 bg-brand-600 text-white rounded hover:bg-brand-700"
                                  >
                                    Save note
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      )}

                      {anyAdditions && (
                        <td className="hidden lg:table-cell px-4 py-3 text-right text-sm tabular font-medium text-emerald-700 dark:text-emerald-400">
                          {additionsForEmp > 0 ? formatCurrency(additionsForEmp) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                      )}
                      <td className="hidden md:table-cell px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
                        {formatCurrency(totalMonthly)}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <PensionBadge pension={emp} />
                          {emp.pension_status === 'enrolled' && (
                            <button
                              onClick={() => togglePension(emp.id)}
                              className="text-gray-400 hover:text-gray-600 p-2 transform transition-transform text-sm"
                              style={{ transform: expandedPensions[emp.id] ? 'rotate(90deg)' : 'rotate(0deg)' }}
                              aria-label="View pension details"
                            >
                              &#9654;
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          data-actions-trigger
                          onClick={(e) => openActionsPopover(emp, e)}
                          className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                            actionsPopover?.emp.id === emp.id
                              ? 'bg-brand-100 text-brand-700'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title="Actions"
                          aria-haspopup="menu"
                          aria-expanded={actionsPopover?.emp.id === emp.id}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="5" r="1.5" />
                            <circle cx="12" cy="12" r="1.5" />
                            <circle cx="12" cy="19" r="1.5" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {/* Actions popover is rendered at the bottom of the page (anchored to the trigger) */}
                    {expandedPensions[emp.id] && emp.pension_status === 'enrolled' && (
                      <tr>
                        <td colSpan="99" className="p-0">
                          <PensionExpandedRow employee={emp} pensionableAdditions={pensionableAddByEmp.get(emp.id) || 0} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredEmployees.length === 0 && activeEmployees.length > 0 && (
                <tr>
                  <td colSpan="99" className="px-4 py-8 text-center text-gray-500">
                    No employees matching "{searchQuery}"
                  </td>
                </tr>
              )}
              {activeEmployees.length === 0 && (
                <tr>
                  <td colSpan="99" className="px-4 py-8 text-center text-gray-500">
                    No active employees. Add employees using the New Starter form.
                  </td>
                </tr>
              )}
              {filteredEmployees.length > 0 && (
                <tr className="bg-gradient-to-r from-brand-50 to-brand-100/60 border-t-2 border-brand-300">
                  <td className="px-4 py-3 text-[12px] uppercase tracking-wider font-bold text-brand-800">Period total</td>
                  <td className="hidden lg:table-cell px-4 py-3" />
                  <td className="px-4 py-3 text-right text-[13px] tabular font-bold text-brand-900">
                    {formatCurrency(salariesTotal)}<span className="text-[11px] font-normal text-brand-700 ml-1">/mo</span>
                  </td>
                  {hourlyTrackingEnabled && (<td className="hidden lg:table-cell px-4 py-3" />)}
                  {commissionEnabled && (
                    <td className="hidden lg:table-cell px-4 py-3 text-right text-[13px] tabular font-bold text-emerald-700">
                      {formatCurrency(commissionTotal)}
                    </td>
                  )}
                  {anyAdditions && (
                    <td className="hidden lg:table-cell px-4 py-3 text-right text-[13px] tabular font-bold text-emerald-700">
                      {formatCurrency(additionsTotal)}
                    </td>
                  )}
                  <td className="hidden md:table-cell px-4 py-3 text-right text-[13px] tabular font-bold text-brand-900">
                    {formatCurrency(totalMonthlyPayroll)}
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3" />
                  <td className="px-4 py-3" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full width, below the employee table — a table needs the room, and
          this is reference rather than something to action every day. */}
      <StarterFormsList invites={starterInvites} onToast={setToast} onChanged={loadData} />

      {actionsPopover && (
        <EmployeeActionsPopover
          emp={actionsPopover.emp}
          anchorRect={actionsPopover.rect}
          sickRecords={sickRecords}
          onClose={() => setActionsPopover(null)}
          onSelectAction={handleActionSelect}
        />
      )}

      {pensionModal && (
        <PensionModal employee={pensionModal} pension={pensionModal} onSave={handlePensionSave} onClose={() => setPensionModal(null)} pensionableAdditions={pensionableAddByEmp.get(pensionModal.id) || 0} />
      )}
      {showPensionSchemeSetup && pensionSchemeDefault?.company_id && (
        <PensionMassSetupModal
          companyId={pensionSchemeDefault.company_id}
          onClose={() => setShowPensionSchemeSetup(false)}
          onSaved={() => { setShowPensionSchemeSetup(false); loadData(); }}
        />
      )}
      {leaveModal && (
        <LeaveModal employee={leaveModal} onSave={handleLeaveSave} onClose={() => setLeaveModal(null)} />
      )}
      {sickModal && (
        <SickModal employee={sickModal} existingSickRecord={sickRecords.find(r => r.employee_id === sickModal.id)} onSave={handleSickSave} onClose={() => setSickModal(null)} />
      )}
      {unpaidLeaveModal && (
        <UnpaidLeaveModal employee={unpaidLeaveModal} onSave={handleUnpaidLeaveSave} onClose={() => setUnpaidLeaveModal(null)} />
      )}
      {additionModal && (
        <AdditionModal
          employee={additionModal}
          onSave={handleAdditionSave}
          onClose={() => setAdditionModal(null)}
          activeParentalLeaves={pendingChanges
            .filter(c => c.change_type === 'parental_leave' && c.employee_id === additionModal.id)
            .map(c => ({
              id: c.id,
              leave_type: c.change_data?.leave_type,
              start_date: c.change_data?.desired_leave_start_date || c.change_data?.start_date,
              expected_return_date: c.change_data?.expected_return_date,
            }))}
        />
      )}
      {deductionModal && (
        <DeductionModal employee={deductionModal} onSave={handleDeductionSave} onClose={() => setDeductionModal(null)} />
      )}
      {bankDetailsModal && (
        <BankDetailsModal employee={bankDetailsModal} onSave={handleBankDetailsSave} onClose={() => setBankDetailsModal(null)} />
      )}
      {addressModal && (
        <AddressModal employee={addressModal} onSave={handleAddressSave} onClose={() => setAddressModal(null)} />
      )}
      {personalDetailsModal && (
        <PersonalDetailsModal employee={personalDetailsModal} onSave={handlePersonalDetailsSave} onClose={() => setPersonalDetailsModal(null)} />
      )}
      {contractualChangeModal && (
        <ContractualChangeModal employee={contractualChangeModal} onSave={handleContractualChangeSave} onClose={() => setContractualChangeModal(null)} />
      )}
      {salaryChangeModal && (
        <SalaryChangeModal employee={salaryChangeModal} onSave={handleSalaryChangeSave} onClose={() => setSalaryChangeModal(null)} />
      )}

      {undoConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setUndoConfirmId(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Undo Change</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to undo this change? This will revert the data to its previous state.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setUndoConfirmId(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">Cancel</button>
              <button onClick={confirmUndo} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">Undo Change</button>
            </div>
          </div>
        </div>
      )}

      {showReviewModal && (() => {
        const grouped = {
          new_starter: pendingChanges.filter(c => c.change_type === 'new_starter'),
          leaver: pendingChanges.filter(c => c.change_type === 'leaver'),
          pension: pendingChanges.filter(c => c.change_type === 'pension'),
          salary: pendingChanges.filter(c => c.change_type === 'salary'),
          parental_leave: pendingChanges.filter(c => c.change_type === 'parental_leave'),
          sick_leave: pendingChanges.filter(c => c.change_type === 'sick_leave'),
          addition: pendingChanges.filter(c => c.change_type === 'addition'),
          deduction: pendingChanges.filter(c => c.change_type === 'deduction'),
          bank_details: pendingChanges.filter(c => c.change_type === 'bank_details'),
          address_change: pendingChanges.filter(c => c.change_type === 'address_change'),
          personal_details: pendingChanges.filter(c => c.change_type === 'personal_details'),
          contractual_change: pendingChanges.filter(c => c.change_type === 'contractual_change'),
        };
        const commissionEntries = employees.filter(e => parseFloat(e.commission) > 0);
        const hourlyEntries = employees.filter(e => e.pay_type === 'hourly' && parseFloat(e.hours_this_period) > 0);
        const hourlyMissing = employees.filter(e => e.pay_type === 'hourly' && (!e.hours_this_period || parseFloat(e.hours_this_period) === 0) && e.status === 'active');
        const totalChanges = pendingChanges.length;
        const periodLabel = currentPeriod
          ? `${taxMonthToName(currentPeriod.tax_month)} ${currentPeriod.tax_year}/${currentPeriod.tax_year + 1}`
          : 'Current Period';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-brand-50 dark:bg-gray-900 rounded-t-xl">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Review Submission</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Payroll period: <span className="font-semibold text-brand-700 dark:text-brand-400">{periodLabel}</span>
                </p>
              </div>

              <div className="p-6 space-y-4">
                {existingSubmission && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded text-sm text-amber-800 dark:text-amber-300">
                    <span className="font-semibold">Warning:</span> You already have a pending submission. Submitting again will replace it.
                  </div>
                )}

                {totalChanges === 0 && commissionEntries.length === 0 && hourlyEntries.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-lg font-medium">No changes this period</p>
                    <p className="text-sm mt-1">Submitting will confirm that no changes are needed.</p>
                  </div>
                )}

                {grouped.new_starter.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-2">New Starters ({grouped.new_starter.length})</h4>
                    <ul className="space-y-1">
                      {grouped.new_starter.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded">
                            {d.first_name} {d.last_name} &mdash; {d.job_title || 'No title'}, {formatCurrency(d.annual_salary || 0)}/yr
                            {d.start_date && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Start date: {d.start_date}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.leaver.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">Leavers ({grouped.leaver.length})</h4>
                    <ul className="space-y-1">
                      {grouped.leaver.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; Last day: {d.leaving_date || d.leave_date || 'Not set'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.salary.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2">Pay Changes ({grouped.salary.length})</h4>
                    <ul className="space-y-1">
                      {grouped.salary.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.new_hourly_rate
                              ? `New rate: ${formatCurrency(d.new_hourly_rate)}/hr`
                              : `New salary: ${formatCurrency(d.new_annual_salary || 0)}/yr (${formatCurrency(d.new_monthly_salary || 0)}/mo)`
                            }
                            {d.effective_date && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Effective: {d.effective_date}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.pension.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-2">Pension Changes ({grouped.pension.length})</h4>
                    <ul className="space-y-1">
                      {grouped.pension.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; Status: {d.status || 'updated'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.parental_leave.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-pink-700 dark:text-pink-400 uppercase tracking-wide mb-2">Parental Leave ({grouped.parental_leave.length})</h4>
                    <ul className="space-y-1">
                      {grouped.parental_leave.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-pink-50 dark:bg-pink-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.leave_type || 'Parental leave'}, from {d.start_date || 'TBD'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.sick_leave.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-orange-700 dark:text-orange-400 uppercase tracking-wide mb-2">Sick Leave ({grouped.sick_leave.length})</h4>
                    <ul className="space-y-1">
                      {grouped.sick_leave.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-orange-50 dark:bg-orange-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; from {d.start_date || 'TBD'}{d.ssp_eligible ? ' (SSP)' : ''}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.addition.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2">Additions ({grouped.addition.length})</h4>
                    <ul className="space-y-1">
                      {grouped.addition.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.addition_type_label}: {formatCurrency(d.amount)}{d.recurring ? ' (Recurring)' : ''}
                            {d.effective_date && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Effective: {d.effective_date}</span>}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.deduction.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">Deductions ({grouped.deduction.length})</h4>
                    <ul className="space-y-1">
                      {grouped.deduction.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.deduction_type_label}: {formatCurrency(d.amount)}{d.recurring ? ' (Recurring)' : ''}
                            {d.effective_date && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Effective: {d.effective_date}</span>}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.bank_details.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-400 uppercase tracking-wide mb-2">Bank Detail Changes ({grouped.bank_details.length})</h4>
                    <ul className="space-y-1">
                      {grouped.bank_details.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-cyan-50 dark:bg-cyan-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; New account: {d.bank_account_name}, {d.sort_code}, ****{(d.account_number || '').slice(-4)}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.address_change.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wide mb-2">Address Changes ({grouped.address_change.length})</h4>
                    <ul className="space-y-1">
                      {grouped.address_change.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-teal-50 dark:bg-teal-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.address}, {d.postcode}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.personal_details.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wide mb-2">Personal Details ({grouped.personal_details.length})</h4>
                    <ul className="space-y-1">
                      {grouped.personal_details.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-teal-50 dark:bg-teal-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {personalDetailsSummary(d)}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {grouped.contractual_change.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wide mb-2">Contractual Changes ({grouped.contractual_change.length})</h4>
                    <ul className="space-y-1">
                      {grouped.contractual_change.map(c => {
                        const d = typeof c.change_data === 'string' ? JSON.parse(c.change_data) : c.change_data;
                        return (
                          <li key={c.id} className="text-sm text-gray-700 dark:text-gray-200 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-2 rounded">
                            {d.employee_name || 'Employee'} &mdash; {d.change_type_label}, effective {d.effective_date || 'TBD'}
                            {d.new_hourly_rate && <span className="block text-xs text-gray-600 dark:text-gray-300 mt-0.5">{formatCurrency(d.new_hourly_rate)}/hr, {d.new_contracted_hours || 0} hrs/wk</span>}
                            {d.change_type === 'hourly_to_salaried' && d.new_annual_salary && <span className="block text-xs text-gray-600 dark:text-gray-300 mt-0.5">{formatCurrency(d.new_annual_salary)} /yr</span>}
                            {d.new_annual_salary && d.change_type !== 'hourly_to_salaried' && d.change_type !== 'salaried_to_hourly' && <span className="block text-xs text-gray-600 dark:text-gray-300 mt-0.5">{formatCurrency(d.new_annual_salary)} /yr</span>}
                            {d.notes && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Note: {d.notes}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {hourlyEntries.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">Hourly Hours ({hourlyEntries.length})</h4>
                    <ul className="space-y-1">
                      {hourlyEntries.map(emp => (
                        <li key={emp.id} className="text-sm text-gray-700 dark:text-gray-200 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 rounded flex justify-between">
                          <span>{emp.first_name} {emp.last_name} <span className="text-xs text-gray-400">({formatCurrency(emp.hourly_rate)}/hr)</span></span>
                          <span className="font-medium">{parseFloat(emp.hours_this_period).toFixed(2)} hrs = {formatCurrency(parseFloat(emp.hours_this_period) * parseFloat(emp.hourly_rate))}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hourlyMissing.length > 0 && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-sm text-amber-700 dark:text-amber-300">
                    <span className="font-medium">Warning:</span> {hourlyMissing.length} hourly employee{hourlyMissing.length !== 1 ? 's have' : ' has'} no hours entered this period: {hourlyMissing.map(e => `${e.first_name} ${e.last_name}`).join(', ')}
                  </div>
                )}

                {commissionEntries.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-brand-700 dark:text-brand-400 uppercase tracking-wide mb-2">Commission ({commissionEntries.length})</h4>
                    <ul className="space-y-1">
                      {commissionEntries.map(emp => (
                        <li key={emp.id} className="text-sm text-gray-700 dark:text-gray-200 bg-brand-50 dark:bg-brand-900/30 px-3 py-2 rounded flex justify-between">
                          <span>{emp.first_name} {emp.last_name}</span>
                          <span className="font-medium">{formatCurrency(emp.commission)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl flex justify-between items-center">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Go Back
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Confirm &amp; Submit
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
